/**
 * Custom Menu Overlay Component
 * Handles opening/closing the menu and switching images on hover
 */

class CustomMenuOverlay extends HTMLElement {
  constructor() {
    super();
    
    this.closeButton = this.querySelector('.custom-menu-overlay__close');
    this.backdrop = this.querySelector('.custom-menu-overlay__backdrop');
    this.menuItems = this.querySelectorAll('.custom-menu-overlay__menu-item');
    this.images = this.querySelectorAll('.custom-menu-overlay__image');
    this.hamburgerButton = null;
    
    this.init();
  }

  init() {
    // Find the hamburger button in the header
    this.hamburgerButton = document.querySelector('.hamburger-menu-button');
    
    // Bind events
    this.closeButton?.addEventListener('click', () => this.close());
    this.backdrop?.addEventListener('click', () => this.close());
    this.hamburgerButton?.addEventListener('click', () => this.open());
    
    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    });
    
    // Setup hover events for menu items to change images
    this.menuItems.forEach((item, index) => {
      item.addEventListener('mouseenter', () => this.switchImage(index));
    });
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    
    // Update hamburger button state
    if (this.hamburgerButton) {
      this.hamburgerButton.setAttribute('aria-expanded', 'true');
    }
    
    // Trap focus within overlay
    this.trapFocus();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    
    // Update hamburger button state
    if (this.hamburgerButton) {
      this.hamburgerButton.setAttribute('aria-expanded', 'false');
      this.hamburgerButton.focus(); // Return focus to button
    }
  }

  switchImage(index) {
    // Hide all images
    this.images.forEach(img => {
      img.classList.remove('custom-menu-overlay__image--active');
    });
    
    // Show the image at the specified index
    const targetImage = this.querySelector(`[data-menu-index="${index}"]`);
    if (targetImage) {
      targetImage.classList.add('custom-menu-overlay__image--active');
    }
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    // Focus first element
    firstElement.focus();
    
    // Handle tab key
    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };
    
    this.addEventListener('keydown', handleTab);
  }
}

// Register the custom element
if (!customElements.get('custom-menu-overlay')) {
  customElements.define('custom-menu-overlay', CustomMenuOverlay);
}

// Also handle direct clicks on buttons with on:click attributes
document.addEventListener('click', (e) => {
  const target = e.target.closest('[on\\:click]');
  if (!target) return;
  
  const action = target.getAttribute('on:click');
  
  if (action === 'custom-menu-overlay/open') {
    e.preventDefault();
    const overlay = document.querySelector('custom-menu-overlay');
    if (overlay) overlay.open();
  } else if (action === '/close') {
    e.preventDefault();
    const overlay = target.closest('custom-menu-overlay');
    if (overlay) overlay.close();
  }
});
